import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { commonDeleteApi } from "../../../redux/reducers/deleteApiSlice";
import { showConfirmAlert } from "../../components-react/ReactAlert";
import PaymentLinkTable from "../../payment-link-system/PaymentLinkTable";
import { delete_hygiene_task } from "../../../data/ApiEndpoints";
import Loader from "../../ui/Loader";
import {
  convertStraight,
  convertTo12HourFormat,
  formatDateTimezon,
} from "../../common-data/Converts";
import TableSkeleton from "../../common-components/TableSkeleton";
import {
  BinIcon,
  CheckCircleIcon,
  EditIcon,
  XCircleIcon,
  InfoCircleIcon,
  ClockIcon,
  DatePickerIcon,
} from "../../../images/icons/icons";
import { hygine_task_frequency_categories } from "../../common-data/DropdowData";
import InfoPopover from "../../pt-trials/pt-reports/re-usablee/InfoPopover";

const NewTaskLists = ({
  userInfoData,
  setModValues,
  isPage,
  setIsPage,
  pageLimit,
  setPageLimit,
  callFunction,
}) => {
  const dispatch = useDispatch();
  const { loading: delApiLoading } = useSelector((state) => state.delApi);
  const { hygieneTasksLoading, hygieneTasksError, hygieneTasks } = useSelector(
    (state) => state.hygiene
  );

  const iniDatas = {
    allDatas: [],
    pagination: [],
  };
  const [hygineData, setHygineData] = useState(iniDatas);
  const [activePopover, setActivePopover] = useState(null);

  const handleInfoClick = (rowData, popoverType, event) => {
    if (event) {
      event.stopPropagation();
    }
    const popoverId = `${rowData.uid}-${popoverType}`;
    setActivePopover(activePopover === popoverId ? null : popoverId);
  };

  const handleDelete = async (filter) => {
    if (filter?.uid) {
      const handleConfirm = async () => {
        try {
          const response = await dispatch(
            commonDeleteApi({
              TOKEN: userInfoData?.token,
              endpoint: delete_hygiene_task,
              queryParams: filter,
            })
          ).unwrap();

          if (response?.status) {
            toast.success(response?.message || "Deleted successfully!");
            callFunction();
          } else {
            toast.error(response?.message || "Can not delete!");
          }
        } catch (error) {
          toast.error(error || "Failed to delete");
        }
      };

      showConfirmAlert(handleConfirm, `Are you sure, you want to delete!`);
    } else {
      toast.error("UID required!");
    }
  };

  useEffect(() => {
    if (
      !hygieneTasksLoading &&
      !hygieneTasksError &&
      hygieneTasks?.status &&
      hygieneTasks?.data?.length > 0
    ) {
      setHygineData((prevs) => ({
        ...prevs,
        allDatas: hygieneTasks.data,
        pagination: hygieneTasks?.pagination || [],
      }));
    } else {
      setHygineData(iniDatas);
    }
  }, [hygieneTasksLoading, hygieneTasksError, hygieneTasks]);

  useEffect(() => {
    return () => {
      setActivePopover(null);
    };
  }, []);

  const tbColumns = [
    {
      dataField: "frequency_type",
      text: "frequency type",
      formatter: (_, row) => {
        return (
          <span className="tb-data tb-badge tb-badge-success-1 minWidth150">
            {row?.frequency_type?.replace(/_/g, " ") || "NA"}
          </span>
        );
      },
    },
    {
      dataField: "priority",
      text: "priority",
      formatter: (_, row) => {
        const taskPriroty = row?.priority || "";
        const priorityColor =
          taskPriroty === "critical"
            ? "#dc3545"
            : taskPriroty === "high"
            ? "#ffc107"
            : taskPriroty === "medium"
            ? "#0d6efd"
            : "#198754";
        const priorityTag =
          taskPriroty === "critical"
            ? "P1"
            : taskPriroty === "high"
            ? "P2"
            : taskPriroty === "medium"
            ? "P3"
            : "P4";

        return (
          <span
            className="tb-data minWidth90"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {row?.priority ? (
              <div
                className="badge"
                style={{
                  width: "100%",
                  height: "30px",
                  borderRadius: "10px",
                  backgroundColor: priorityColor,
                  color: "#fff",
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  fontSize: "13px",
                  letterSpacing: "1px",
                  fontWeight: "500",
                }}
              >
                ({priorityTag}) {row?.priority || "-"}
              </div>
            ) : (
              "-"
            )}
          </span>
        );
      },
    },
    {
      dataField: "name",
      text: "task name",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {row?.name?.replace(/_/g, " ") || "NA"}
          </span>
        );
      },
    },
    {
      dataField: "category",
      text: "task category",
      formatter: (_, row) => {
        return <span className="tb-data">{row?.category_name || "-"}</span>;
      },
    },
    {
      dataField: "media_required",
      text: "media",
      formatter: (_, row) => {
        return (
          <span className="tb-data icon">
            {row?.media_required ? (
              <CheckCircleIcon color="#209216" />
            ) : (
              <XCircleIcon color="#E41815" />
            )}
          </span>
        );
      },
    },
    {
      dataField: "media_type",
      text: "media type",
      formatter: (_, row) => {
        return <span className="tb-data">{row?.media_type || "-"}</span>;
      },
    },
    {
      dataField: "img_quantity",
      text: "image",
      formatter: (_, row) => {
        return <span className="tb-data">{row?.img_quantity || "-"}</span>;
      },
    },
    {
      dataField: "vid_quantity",
      text: "video",
      formatter: (_, row) => {
        return <span className="tb-data">{row?.vid_quantity || "-"}</span>;
      },
    },
    {
      dataField: "scheduled_date",
      text: "scheduled date",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {convertStraight(row?.scheduled_date || "")}
          </span>
        );
      },
    },
    {
      dataField: "task_times",
      text: "task times",
      formatter: (_, row) => {
        const taskTimes = row?.task_times || [];
        const popoverData = taskTimes.map((time, index) => {
          const hasDate = time.date && time.date !== null;
          return {
            label: hasDate ? `Slot ${index + 1}` : `Time ${index + 1}`,
            value: hasDate
              ? `${convertStraight(time.date)} | ${convertTo12HourFormat(
                  time.start_time
                )} - ${convertTo12HourFormat(time.end_time)}`
              : `${convertTo12HourFormat(
                  time.start_time
                )} - ${convertTo12HourFormat(time.end_time)}`,
            fallback: "-",
            icon: hasDate ? (
              <DatePickerIcon color="#2c3e50" />
            ) : (
              <ClockIcon color="#2c3e50" />
            ),
          };
        });

        return (
          <div
            className="tb-flex link"
            onClick={(e) => handleInfoClick(row, "task_times", e)}
          >
            <span className="tb-data tb-link tb-info-icon">
              <InfoCircleIcon color="blue" />
              <InfoPopover
                isVisible={activePopover === `${row.uid}-task_times`}
                onClose={() => setActivePopover(null)}
                title="Task Times"
                data={popoverData}
                position="bottom"
              />
            </span>
            <span className="tb-data">view</span>
          </div>
        );
      },
    },
    {
      dataField: "week",
      text: "week",
      formatter: (_, row) => {
        if (!row?.week) {
          return <span className="tb-data">-</span>;
        }

        const weekDays = row.week.split(",").map((day) => day.trim());
        const popoverData = weekDays.map((day, index) => ({
          label: `Day ${index + 1}`,
          value: day.charAt(0).toUpperCase() + day.slice(1),
          fallback: "-",
          icon: <DatePickerIcon color="#2c3e50" />,
        }));

        return (
          <div
            className="tb-flex link"
            onClick={(e) => handleInfoClick(row, "week", e)}
          >
            <span className="tb-data tb-link tb-info-icon">
              <InfoCircleIcon />
              <InfoPopover
                isVisible={activePopover === `${row.uid}-week`}
                onClose={() => setActivePopover(null)}
                title="Week Days"
                data={popoverData}
                position="bottom"
              />
            </span>
            <span className="tb-data">view</span>
          </div>
        );
      },
    },
    {
      dataField: "month_date_range",
      text: "monthly range",
      formatter: (_, row) => {
        return <span className="tb-data minWidth200">{row?.month_date_range || "-"}</span>;
      },
    },
    {
      dataField: "incremental_value",
      text: "repeat",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth150">
            {row?.incremental_value || ""} (
            {hygine_task_frequency_categories.find(
              (item) => item?.value === row?.frequency_type
            )?.label || ""}
            )
          </span>
        );
      },
    },
    {
      dataField: "end_after",
      text: "end",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth80">
            {row?.end_after === "-1"
              ? "never"
              : `${row?.end_after || ""} times`}
          </span>
        );
      },
    },
    {
      dataField: "stakeholders",
      text: "stakeholders",
      formatter: (_, row) => {
        return <span className="tb-data">{row?.stakeholders || "NA"}</span>;
      },
    },
    {
      dataField: "description",
      text: "task description",
      formatter: (_, row) => {
        return (
          <span
            className="tb-data link"
            onClick={() =>
              setModValues((prevs) => ({
                ...prevs,
                isModOpen: true,
                rowData: row,
                cardName: "task_description",
              }))
            }
          >
            read
          </span>
        );
      },
    },
    {
      dataField: "date_added",
      text: "created on",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth100">
            {row?.date_added ? formatDateTimezon(row.date_added) : "-"}
          </span>
        );
      },
    },
    {
      dataField: "status",
      text: "status",
      formatter: (_, row) => {
        return (
          <span
            className="tb-data minWidth100 withPadding borderRdius8"
            style={{
              background: row?.status === "active" ? "#379C00" : "#C80000",
              color: "#fff",
            }}
          >
            {row?.status?.replace(/_/g, " ") || "NA"}
          </span>
        );
      },
    },
    {
      dataField: "action",
      text: "Action",
      formatter: (_, row) => (
        <div className="hygiene-action-btn-container">
          <button
            className="actionBtn icon edit"
            onClick={() =>
              setModValues((prevs) => ({
                ...prevs,
                isModOpen: true,
                rowData: row,
                cardName: "edit",
              }))
            }
          >
            <EditIcon />
          </button>

          <button
            className="actionBtn icon delete"
            onClick={() => {
              const uidFilter = {
                uid: row?.uid || "",
              };
              handleDelete(uidFilter);
            }}
          >
            {delApiLoading ? <Loader /> : <BinIcon />}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="hygiene-tb-container mt30">
      {hygieneTasksLoading ? (
        <TableSkeleton />
      ) : (
        <PaymentLinkTable
          tableData={hygineData.allDatas}
          tableColumns={tbColumns}
          tablePagination={hygineData.pagination}
          isPage={isPage}
          setIsPage={(value) => setIsPage(value)}
          pageLimit={pageLimit}
          setPageLimit={setPageLimit}
        />
      )}
    </div>
  );
};

export default NewTaskLists;
